import {
  IllegalOperationException,
} from '../../../exceptions/illegal-operation-exception.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { EventTicket } from '../event-ticket.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { VersionRange } from '../../core/version-range.js';
import { BinaryHelper } from '../../../serialization/binary-helper.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData, cloneMap,
  isSameMap, mapToData, isSameData, isSameList, cloneAsSet,
} from '../../dto/dto.js';
import { TicketTemplate } from '../ticket-template';
import {
  ITicketPool,
} from './ticket-pool.js';
import { TicketPoolStrategy } from './ticket-pool-strategy.js';
import { ITicketPoolSeatingModule } from './ticket-pool-seating-module.js';

/**
 * @deprecated
 */
export class LegacyTicketPool extends ITicketPool implements ITicketPoolSeatingModule {
  static readonly TYPE_ID: string = '36a6cd5fd79340329c414c3e28f94206';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly eventId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly reservedSeats: Set<number>,
    public readonly soldTickets: Map<string, number>,
    public readonly reservedTickets: Map<string, number>,
  ) {
    super();
  }

  public readonly strategy: TicketPoolStrategy = TicketPoolStrategy.Legacy;

  get $typeId(): string {
    return LegacyTicketPool.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyTicketPool.SCHEMA_VERSION);
  }

  public getSeatingModule(): ITicketPoolSeatingModule | null {
    return this;
  }

  public getTicketCount(templateId: string): number {
    return this.getSoldTicketCount(templateId) + this.getReservedTicketCount(templateId);
  }

  public isSoldOut(template: TicketTemplate): boolean {
    if(template.quantity.isInfinite) return false;

    return this.getTicketCount(template.uniqueId) >= template.quantity.value;
  }

  isAnySeatReserved(seats: number[]): boolean {
    for(let i = 0; i < seats.length; i++) {
      if(this.reservedSeats.has(seats[i])) {
        return true;
      }
    }

    return false;
  }

  isSeatReserved(seat: number): boolean {
    return this.reservedSeats.has(seat);
  }

  getSoldTicketCount(templateId: string): number {
    return this.soldTickets.get(templateId) ?? 0;
  }

  getReservedTicketCount(templateId: string): number {
    return this.reservedTickets.get(templateId) ?? 0;
  }

  reserveSeat(seatId: number): boolean {
    if(this.reservedSeats.has(seatId)) {
      return false;
    }

    this.reservedSeats.add(seatId);
    return true;
  }

  freeSeat(seatId: number): boolean {
    if(!this.reservedSeats.has(seatId)) {
      return false;
    }

    this.reservedSeats.delete(seatId);
    return true;
  }

  reserveTicket(ticket: EventTicket): boolean {
    const seatingOptions = ticket.seatingOptions;

    if(isNotNullOrUndefined(seatingOptions)) {
      if(this.reservedSeats.has(seatingOptions.seatId)) {
        return false;
      }
    }
    else {
      if(this.isSoldOut(ticket.template)) {
        return false;
      }
    }

    const currentReservedAmount = this.getReservedTicketCount(ticket.templateId);
    this.reservedTickets.set(ticket.templateId, currentReservedAmount + 1);

    if(isNotNullOrUndefined(seatingOptions)) {
      this.reservedSeats.add(seatingOptions.seatId);
    }

    return true;
  }

  reserveTicketWithoutLimit(ticket: EventTicket): boolean {
    const seatingOptions = ticket.seatingOptions;
    const templateId = ticket.template.uniqueId;

    if(isNotNullOrUndefined(seatingOptions)) {
      if(this.reservedSeats.has(seatingOptions.seatId)) {
        return false;
      }
    }

    const currentReservedAmount = this.getReservedTicketCount(templateId);
    this.reservedTickets.set(templateId, currentReservedAmount + 1);

    if(isNotNullOrUndefined(seatingOptions)) {
      this.reservedSeats.add(seatingOptions.seatId);
    }

    return true;
  }

  addReservationWithoutLimit(templateId: string, quantity: number): void {
    if(Number.isSafeInteger(quantity) === false) {
      throw new IllegalOperationException('You are trying to reserve a non-integer amount of tickets.');
    }

    if(quantity < 0) {
      throw new IllegalOperationException('You are trying to reserve a negative amount of tickets.');
    }

    if(quantity === 0) {
      return;
    }

    const currentReservedAmount = this.getReservedTicketCount(templateId);

    this.reservedTickets.set(templateId, currentReservedAmount + quantity);
  }

  removeReservation(templateId: string, quantity: number): void {
    if(Number.isSafeInteger(quantity) === false) {
      throw new IllegalOperationException('You are trying to remove a non-integer amount of tickets.');
    }

    if(quantity < 0) {
      throw new IllegalOperationException('You are trying to remove a negative amount of tickets.');
    }

    if(quantity === 0) {
      return;
    }

    const currentReservedAmount = this.getReservedTicketCount(templateId);
    if(currentReservedAmount < quantity) {
      throw new IllegalOperationException('You are trying to remove more tickets than you have reserved.');
    }

    this.reservedTickets.set(templateId, currentReservedAmount - quantity);
  }

  reserveAndSell(ticket: EventTicket): boolean {
    const reserved = this.reserveTicket(ticket);
    if(reserved) {
      this.onTicketSold(ticket);
    }

    return reserved;
  }

  reserveAndSellWithoutLimit(ticket: EventTicket): boolean {
    const reserved = this.reserveTicketWithoutLimit(ticket);
    if(reserved) {
      this.onTicketSold(ticket);
    }

    return reserved;
  }

  onTicketAborted(ticket: EventTicket): void {
    const currentReservedAmount = this.getReservedTicketCount(ticket.templateId);
    if(currentReservedAmount <= 0) {
      throw new IllegalOperationException('You are trying to refund tickets that have not been reserved.');
    }

    this.reservedTickets.set(ticket.templateId, currentReservedAmount - 1);

    const seatingOptions = ticket.seatingOptions;
    if(isNotNullOrUndefined(seatingOptions)) {
      this.reservedSeats.delete(seatingOptions.seatId);
    }
  }

  onTicketRefundedOrCanceled(ticket: EventTicket): void {
    const currentSoldAmount = this.getSoldTicketCount(ticket.templateId);
    if(currentSoldAmount <= 0) {
      throw new IllegalOperationException('You are trying to refund tickets that have not been sold.');
    }

    this.soldTickets.set(ticket.templateId, currentSoldAmount - 1);

    const seatingOptions = ticket.seatingOptions;
    if(isNotNullOrUndefined(seatingOptions)) {
      this.reservedSeats.delete(seatingOptions.seatId);
    }
  }

  onTicketSold(ticket: EventTicket): void {
    const currentReservedAmount = this.getReservedTicketCount(ticket.templateId);
    if(currentReservedAmount <= 0) {
      throw new IllegalOperationException(`You are trying to sell tickets that have not been reserved for template {${ticket.templateId}} of event {${ticket.eventId}} of {${ticket.businessRef}}.`);
    }

    const currentSoldAmount = this.getSoldTicketCount(ticket.templateId);

    this.reservedTickets.set(ticket.templateId, currentReservedAmount - 1);
    this.soldTickets.set(ticket.templateId, currentSoldAmount + 1);
  }

  static empty(businessRef: BusinessModuleRef, eventId: string): LegacyTicketPool {
    return new LegacyTicketPool(
        eventId,
        businessRef,
        new Set<number>(),
        new Map<string, number>(),
        new Map<string, number>(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyTicketPool {
    const rawData: unknown = MapUtils.getRaw(map, 'reservedSeats');
    const seats: number[] = isNotNullOrUndefined(rawData) ?
        BinaryHelper.fromBytesToIntegers(context.fromBinaryBlob(rawData)) : [];

    return new LegacyTicketPool(
        MapUtils.get<string>(map, 'eventId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        new Set<number>(seats),
        MapUtils.getMap(map, 'soldTickets', (k, v) => ({
          key: k,
          value: Number(v),
        })),
        MapUtils.getMap(map, 'reservedTickets', (k, v) => ({
          key: k,
          value: Number(v),
        }))
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'businessRef': this.businessRef.encode(),
      'reservedSeats': context.toBinaryBlob(BinaryHelper.fromIntegersToBytes([...this.reservedSeats])),
      'soldTickets': mapToData(this.soldTickets, (k, v) => ({
        key: k,
        value: v,
      })),
      'reservedTickets': mapToData(this.reservedTickets, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return new LegacyTicketPool(
        this.eventId,
        this.businessRef.clone(),
        cloneAsSet(this.reservedSeats),
        cloneMap(this.soldTickets, (v) => v),
        cloneMap(this.reservedTickets, (v) => v),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.soldTickets, this.soldTickets, (lh, rh) => lh === rh) &&
          isSameMap(other.reservedTickets, this.reservedTickets, (lh, rh) => lh === rh) &&
          other.eventId === this.eventId &&
          isSameList([...other.reservedSeats], [...this.reservedSeats]) && // this is a horrible way to do it
          isSameData(other.businessRef, this.businessRef);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof LegacyTicketPool;
  }

  static assertType(order: DataModel): order is LegacyTicketPool {
    return order instanceof LegacyTicketPool;
  }
}

export class LegacyTicketPoolBuilder extends DataModelBuilder<LegacyTicketPool> {
  build(context: SerializationContext, map: SerializedData): LegacyTicketPool {
    return LegacyTicketPool.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyTicketPool.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LegacyTicketPool.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `seats` field of type `number[]` was introduced.
    if(version < 2) {
      version = 2;
    }

    // In this version the `seats` field was renamed to `reservedSeats`.
    if(version < 3) {
      version = 3;
      map['reservedSeats'] = map['seats'];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
